/**
 * The physics side of the game: what a body *is*, how one is made, and the
 * two queries the whole thing turns on: what an explosion touches, and
 * whether one thing can see another.
 *
 * The world has no gravity. This is a plan view: down the screen is north,
 * not down. Everything that would otherwise slide forever is held by linear
 * and angular damping instead, which is also what makes a tank feel heavy.
 *
 * Nothing here knows the rules of the game. `game.ts` owns those and calls in.
 */

import {
  AABB,
  type Body,
  BoxShape,
  CircleShape,
  Vec2,
  World,
  type Fixture,
} from "planck";

/**
 * The palette. Cream graph paper is the ground, ink is every outline, yellow
 * is the brand field and green the portfolio field; acid is the marker.
 */
export const palette = {
  ink: "#191919",
  cream: "#fffff0",
  yellow: "#ffd212",
  acid: "#ffed00",
  green: "#02602a",
  greenLight: "#176d3c",
} as const;

/**
 * Collision categories. Friendly fire is off by filter rather than by a check
 * in a callback: a shell simply cannot touch the tank that fired it. Blasts
 * ignore all of this and work on distance, so a barrel hurts everyone.
 *
 * `STRAY` is the exception that proves it: a shell of yours that has come off
 * a wall changes to this category and is nobody's friend. Box2D asks *both*
 * sides before it lets two things touch, so a bounced shell is only dangerous
 * because `PLAYER` lets `STRAY` in as well.
 */
export const CAT = {
  WALL: 0x0001,
  PLAYER: 0x0002,
  ENEMY: 0x0004,
  PSHELL: 0x0008,
  ESHELL: 0x0010,
  DEBRIS: 0x0020,
  PICKUP: 0x0040,
  PROP: 0x0080,
  STRAY: 0x0100,
} as const;

const ALL = 0xffff;

export const MASK = {
  WALL: ALL,
  PLAYER: CAT.WALL | CAT.ENEMY | CAT.ESHELL | CAT.STRAY | CAT.DEBRIS | CAT.PICKUP | CAT.PROP,
  ENEMY: CAT.WALL | CAT.PLAYER | CAT.ENEMY | CAT.PSHELL | CAT.STRAY | CAT.DEBRIS | CAT.PROP,
  PSHELL: CAT.WALL | CAT.ENEMY | CAT.PROP,
  /** A bounced shell of yours: everything the shell hit before, and you. */
  STRAY: CAT.WALL | CAT.ENEMY | CAT.PROP | CAT.PLAYER,
  ESHELL: CAT.WALL | CAT.PLAYER | CAT.PROP,
  DEBRIS: CAT.WALL | CAT.PLAYER | CAT.ENEMY | CAT.DEBRIS | CAT.PROP,
  PICKUP: CAT.PLAYER,
  PROP: CAT.WALL | CAT.PLAYER | CAT.ENEMY | CAT.PSHELL | CAT.ESHELL | CAT.STRAY | CAT.DEBRIS | CAT.PROP,
} as const;

export type Team = "player" | "enemy";

export type PowerName = "cannon" | "triple" | "bouncer" | "heavy";
/** A pickup that helps rather than arms: patches armour, or takes hits for you. */
export type Perk = "repair" | "shield";

export type EntityKind =
  | "wall"
  | "tile"
  | "prop"
  | "barrel"
  | "player"
  | "enemy"
  | "turret"
  | "boss"
  | "shell"
  | "debris"
  | "pickup"
  | "exit"
  | "wreck"
  | "factory";

/**
 * What hangs off a body's user data. One loose shape for every kind rather
 * than a union, because the draw pass reads it fifty times a frame and a
 * union would be fifty narrowings.
 */
export type Entity = {
  kind: EntityKind;
  hp: number;
  maxHp: number;
  fill: string;
  stroke: string;
  lw: number;
  /** Drawing: a box has half-extents, a circle a radius. */
  hw?: number;
  hh?: number;
  r?: number;
  team?: Team;
  power?: PowerName;
  perk?: Perk;
  /** Which tank this is, for the drawing: a player id or an enemy variant. */
  style?: string;
  /** What the driver is doing this frame, for lights: -1 back, 0 coasting, 1 ahead. */
  throttle?: number;
  speed?: number;
  /** Enemy mind. */
  mode?: "patrol" | "hunt" | "search" | "flee";
  goal?: { x: number; y: number };
  seen?: { x: number; y: number };
  modeTime?: number;
  stun?: number;
  /** A mortar round: flies over everything and goes off where it was aimed. */
  mortar?: boolean;
  /** A car's alarm, seconds left of it going off. */
  alarm?: number;
  /** A car that drives the roads, and which way it is going (quarter turns). */
  route?: boolean;
  lane?: number;
  /** Cars stopped behind something, so they do not push through it. */
  wait?: number;
  /** The player's gauges, drawn over the tank: boost left, and the gun. */
  boost?: number;
  gun?: { shots: number; heavy: boolean; bouncy: boolean; left: number };
  /** The player's paint job. */
  paint?: { colour: string; decal: string };
  /** A dropped pickup's whole life, so a ring can show how much is left. */
  fuse?: number;
  /** A drone committed to its ram, which it shows with a red ring. */
  dive?: boolean;
  /** A shell of yours that has bounced, and will hurt you now. */
  stray?: boolean;
  /** Shells: how many ricochets are left, and what a hit takes off. */
  bounces?: number;
  damage?: number;
  /** A shell that goes off rather than simply landing, and how wide. */
  blast?: number;
  /** Seconds left before it goes on its own: shells and debris. */
  ttl?: number;
  /** A hedge is driven through rather than shot through. */
  crushable?: boolean;
  /** Counts down after a hit, so the sprite can flash. */
  flash?: number;
  /** The exit is drawn barred until the level is clear. */
  open?: boolean;
  dead?: boolean;
  /** Set for a tile, so debris comes off in the right colour. */
  ch?: string;
  /** Where the gun points, in world radians. Hulls turn; turrets aim. */
  turret?: number;
  /** Counts down after firing, so the barrel can kick back. */
  recoil?: number;
  /** A second gun, for the boss. */
  turret2?: number;
  /** How far the tracks have rolled, for the tread pattern. */
  roll?: number;
  /** The boss's phase, 0 to 2. */
  phase?: number;
};

export const entityOf = (body: Body): Entity | null => {
  const data = body.getUserData();
  return data && typeof data === "object" && "kind" in data ? (data as Entity) : null;
};

export const bodyOf = (fixture: Fixture): { body: Body; entity: Entity | null } => {
  const body = fixture.getBody();
  return { body, entity: entityOf(body) };
};

export const colours = {
  ...palette,
  concrete: "#d9d9c6",
  brick: "#ffd212",
  hedge: "#3f8f56",
  crate: "#c9a227",
  car: "#d6362b",
  enemy: "#8a2f26",
  boss: "#5a1410",
} as const;

export const makeWorld = (): World => new World({ gravity: new Vec2(0, 0) });

type BoxOpts = {
  density?: number;
  friction?: number;
  restitution?: number;
  angle?: number;
  category: number;
  mask: number;
  bullet?: boolean;
  sensor?: boolean;
  linearDamping?: number;
  angularDamping?: number;
};

/** The one place a body is made, so no fixture escapes without a filter. */
export function makeBox(
  world: World,
  x: number,
  y: number,
  hw: number,
  hh: number,
  entity: Entity,
  opts: BoxOpts & { static?: boolean },
): Body {
  const body = world.createBody({
    type: opts.static ? "static" : "dynamic",
    position: new Vec2(x, y),
    angle: opts.angle ?? 0,
    linearDamping: opts.linearDamping ?? 0.9,
    angularDamping: opts.angularDamping ?? 1.6,
    bullet: opts.bullet ?? false,
  });
  body.createFixture({
    shape: new BoxShape(hw, hh),
    density: opts.density ?? 1,
    friction: opts.friction ?? 0.4,
    restitution: opts.restitution ?? 0.05,
    isSensor: opts.sensor ?? false,
    filterCategoryBits: opts.category,
    filterMaskBits: opts.mask,
  });
  body.setUserData({ ...entity, hw, hh });
  return body;
}

export function makeCircle(
  world: World,
  x: number,
  y: number,
  r: number,
  entity: Entity,
  opts: BoxOpts & { static?: boolean },
): Body {
  const body = world.createBody({
    type: opts.static ? "static" : "dynamic",
    position: new Vec2(x, y),
    linearDamping: opts.linearDamping ?? 0.6,
    angularDamping: opts.angularDamping ?? 1,
    bullet: opts.bullet ?? false,
  });
  body.createFixture({
    shape: new CircleShape(r),
    density: opts.density ?? 1,
    friction: opts.friction ?? 0.3,
    restitution: opts.restitution ?? 0.1,
    isSensor: opts.sensor ?? false,
    filterCategoryBits: opts.category,
    filterMaskBits: opts.mask,
  });
  body.setUserData({ ...entity, r });
  return body;
}

export const FILTER = { CAT, MASK } as const;

// ---- the two queries -------------------------------------------------------

export type BlastHit = { body: Body; entity: Entity; distance: number };

/**
 * Everything within `radius` of a point, with the distance to it.
 *
 * A blast is worked out here and applied by the caller: the world is locked
 * while contacts are being handled, so nothing may be destroyed from inside
 * one, and the game keeps a list to deal with afterwards.
 */
export function blast(world: World, x: number, y: number, radius: number): BlastHit[] {
  const centre = new Vec2(x, y);
  const box = new AABB(new Vec2(x - radius, y - radius), new Vec2(x + radius, y + radius));
  const seen = new Set<Body>();
  const hits: BlastHit[] = [];

  world.queryAABB(box, (fixture: Fixture) => {
    const body = fixture.getBody();
    if (seen.has(body)) return true;
    const entity = entityOf(body);
    if (!entity || entity.dead) return true;
    const d = Vec2.distance(body.getPosition(), centre);
    if (d > radius) return true;
    seen.add(body);
    hits.push({ body, entity, distance: d });
    return true;
  });

  return hits;
}

/** Push everything loose away from a blast, hardest at the centre. */
export function shove(hits: BlastHit[], x: number, y: number, radius: number, power: number): void {
  for (const { body, distance } of hits) {
    if (!body.isDynamic()) continue;
    const away = Vec2.sub(body.getPosition(), new Vec2(x, y));
    const length = away.length();
    const dir = length > 0.001 ? Vec2.mul(away, 1 / length) : new Vec2(0, -1);
    const falloff = 1 - Math.min(1, distance / radius);
    body.applyLinearImpulse(
      Vec2.mul(dir, power * falloff * body.getMass()),
      body.getWorldCenter(),
      true,
    );
    body.applyAngularImpulse((Math.random() - 0.5) * power * falloff * 0.3, true);
  }
}

/**
 * Can a shot from `from` reach `to` without meeting something solid first?
 *
 * Planck hands ray hits over in tree order rather than by distance, so the
 * callback keeps the nearest and returns the fraction to narrow the ray as it
 * goes. Sensors and anything on the shooter's own side are waved through.
 */
export function lineOfSight(
  world: World,
  from: Vec2,
  to: Vec2,
  opts: { ignore?: Body; team?: Team } = {},
): boolean {
  let nearest = 1;
  let blocked = false;

  world.rayCast(from, to, (fixture: Fixture, _point: Vec2, _normal: Vec2, fraction: number) => {
    if (fixture.isSensor()) return -1;
    const body = fixture.getBody();
    if (body === opts.ignore) return -1;
    const entity = entityOf(body);
    if (!entity || entity.dead) return -1;
    if (entity.kind === "pickup" || entity.kind === "exit" || entity.kind === "debris") return -1;
    // A gunner does not shoot through its own side, but seeing past one is
    // fine, the shot would be blocked by the filter anyway.
    if (opts.team && entity.team === opts.team) return -1;
    if (entity.kind === "player" || entity.kind === "enemy" || entity.kind === "boss") return -1;
    if (fraction < nearest) {
      nearest = fraction;
      blocked = true;
    }
    return fraction;
  });

  return !blocked;
}

/** The first solid thing along a ray, as a fraction of its length. 1 is clear. */
export function feel(world: World, from: Vec2, to: Vec2, ignore?: Body): number {
  let nearest = 1;
  world.rayCast(from, to, (fixture: Fixture, _p: Vec2, _n: Vec2, fraction: number) => {
    if (fixture.isSensor()) return -1;
    const body = fixture.getBody();
    if (body === ignore) return -1;
    const entity = entityOf(body);
    if (!entity || entity.dead || entity.kind === "pickup" || entity.kind === "exit") return -1;
    if (fraction < nearest) nearest = fraction;
    return fraction;
  });
  return nearest;
}

/** Every body in the world, as an array, safe to destroy from while walking. */
export function bodies(world: World): Body[] {
  const out: Body[] = [];
  for (let b = world.getBodyList(); b; b = b.getNext()) out.push(b);
  return out;
}

type Point = { x: number; y: number };

/**
 * Did two moving points come within `r` of each other during one step? Each
 * moves in a straight line from its first point to its second, so this is the
 * closest approach of the two, which catches shells fast enough to swap
 * places inside a single step without ever overlapping at either end of it.
 */
export function sweptHit(a0: Point, a1: Point, b0: Point, b1: Point, r: number): boolean {
  // Work in b's frame: a starts at d0 and moves by v.
  const d0x = a0.x - b0.x;
  const d0y = a0.y - b0.y;
  const vx = a1.x - a0.x - (b1.x - b0.x);
  const vy = a1.y - a0.y - (b1.y - b0.y);
  const vv = vx * vx + vy * vy;
  const t = vv > 1e-12 ? Math.min(1, Math.max(0, -(d0x * vx + d0y * vy) / vv)) : 0;
  const cx = d0x + vx * t;
  const cy = d0y + vy * t;
  return cx * cx + cy * cy <= r * r;
}
